import * as yup from "yup";

import { AccountantService, ClientService } from "../../services";
import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedDropDownRHFWithLabel,
	EnhancedSnackbar,
	LabelPosition,
	RHookFormTextFieldWithLabel,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { Box, Button, Card, Grid, Typography, useTheme } from "@mui/material";
import {
	CLIENT_SETUP_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { IEmployeePermissionData, PermissionsService } from "../../services/PermissionsService";
import { IGetListRequest, IResponse, IServerError } from "../../models/common";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	checkSINValidity,
	formatDate,
	scrollPanelFieldsToTop,
	sortDDLByLabel,
	sortData,
	useNavigateToLogout,
} from "../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Avatar } from "primereact/avatar";
import { BasicSetupFormToolbar } from "../BasicSetupFormToolbar";
import { CityService } from "../../services/CityService";
import { CompanyStatutoryHolidayService } from "../../services/CompanyStatutoryHolidayService";
import { CountryService } from "../../services/common/CountryService";
import { DDLDataTable } from "../DDLDataTable";
import { Divider } from "primereact/divider";
import { EmployeeAdditionalInformationFieldsPanel } from "./Panels/EmployeeAdditionalInformationFieldsPanel";
import { EmployeeAddressSetupFieldsPanel } from "./Panels/EmployeeAddressSetupFieldsPanel";
import { EmployeeBankSetupFieldsPanel } from "./Panels/EmployeeBankSetupFieldsPanel";
import { EmployeeInfoSetupFieldsPanel } from "./Panels/EmployeeInfoSetupFieldsPanel";
import { EmployeePayrollSetupFieldsPanel } from "./Panels/EmployeePayrollSetupFieldsPanel";
import { EmployeeSINSetupFieldsPanel } from "./Panels/EmployeeSINSetupFieldsPanel";
import { EmployeeService } from "../../services/EmployeeService";
import { EmployeeSetupToolbar } from "./EmployeeSetupToolbar";
import { EmployeeSetupValidationSchema } from "./validator";
import { EmployeeStatutoryHolidaySetupFieldsPanel } from "./Panels/EmployeeStatutoryHolidaySetupFieldsPanel";
import { EmployeeStubSetupFieldsPanel } from "./Panels/EmployeeStubSetupFieldsPanel";
import { EnhancedSnackbarForLinkMessage } from "../EnhancedSnackbarForLinkMessage";
import { GeneralSettingsService } from "../../services/GeneralSettingsService";
import { IAppState } from "../../store/reducers";
import { IDropDownListResponse } from "../../models/common/IDropDownListResponse";
import { InputText } from "primereact/inputtext";
import { PersonalInfoTabValidationSchema } from "./personalInfoTabValidation";
import { StepperForm } from "./StepperForm";
import { T4SlipService } from "../../services/T4SlipService";
import { TwoColumnLayout } from "./TwoColumnLayout";
import { bankTabValidationSchema } from "./bankTabValidation";
import { payrollTabValidationSchema } from "./payrollTabValidation";
import { setSelectedActivityLogActions } from "../../store/actions/activityLogActions";
import { setSelectedDashboardApisData } from "../../store/actions/dashboardApisDataActions";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions/stickyNavBarActions";
import { yupResolver } from "@hookform/resolvers/yup";

// import { StandardPanel } from "./StandardPanel";
// import { TwoColumnLayout } from "./TwoColumnLayout";

export interface IEmployee { }
const SINGLE_RECORD_DATA_QUERY_KEY = "employee-single-data-record";
const EMPLOYEE_DDL_DATA_QUERY_KEY = "employee-ddl-data-record";
const STATORY_HOLIDAY_RECORD_FORM_DATA_QUERY_KEY = "statutory-holiday-form-data-record";

const useGetData = (
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, id, navigateToLogout],
		queryFn: (data) => {
			return EmployeeService.getById(id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

// const useGetCountryDDL = () =>
//   useQuery({
//     queryKey: ["country-ddl"],
//     queryFn: (data) => {
//       return CountryService.getCountryDDL();
//     },
//     refetchOnWindowFocus: false,
//   });

const useGetEmployeeDDLItems = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: [EMPLOYEE_DDL_DATA_QUERY_KEY, clientId, taxYear],
		queryFn: (data) => {
			return T4SlipService.getEmployeeDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

const useGetPayFrequencyDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: ["pay-frequency-ddl"],
		queryFn: (data) => {
			return EmployeeService.getPayFrequencyDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});


const useGetEmployeePermissionData = (
	clientId: string,
	payrollScheduleId: string,
	navigateToLogout: (path: string) => void,
	employeeId?: string | null,
) =>
	useQuery({
		queryKey: ["get-employee-permission-data", clientId, payrollScheduleId, employeeId],
		queryFn: (data) => {
			return PermissionsService.getEmployeePermissionData(
				clientId,
				payrollScheduleId,
				navigateToLogout,
				employeeId
			);
		},
		refetchOnWindowFocus: false,
	});


const CLIENT_INFO_DATA_QUERY_KEY = "client-info-data-record";
const useGetClientInformationById = (
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [CLIENT_INFO_DATA_QUERY_KEY, id, navigateToLogout],
		queryFn: (data) => {
			return ClientService.getSimpleClientById(id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const GENERAL_SETTING_INFO_DATA_QUERY_KEY = "general-settings-info-data-record";
const useGetGeneralSettingsData = (
	clientId: string,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [GENERAL_SETTING_INFO_DATA_QUERY_KEY, clientId, navigateToLogout],
		queryFn: () => {
			return GeneralSettingsService.getByClientId(clientId, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useGetDataStatutoryHolidayForm = (
	clientId: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [STATORY_HOLIDAY_RECORD_FORM_DATA_QUERY_KEY, clientId, navigateToLogout],
		queryFn: () => {
			return CompanyStatutoryHolidayService.getStatutoryHolidayFormDataById(clientId, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const stepperTabs = [
	{ label: "Personal Information" },
	{ label: "Payroll" },
	{ label: "Bank" },
];
export const EmployeeSetup: React.FC<IEmployee> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const isPendingActivityLogData = useSelector(
		(state: IAppState) => state.isLoadAxiosCall.isLoading
	);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	// const companyInformation = useSelector((state: IAppState) => state.dashboardApisData.client_detail)
	const navigateToLogout = useNavigateToLogout();

	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const canada = useSelector((state: IAppState) => state.countryAndProvincesInfo.canada);
	const countryList = useSelector(
		(state: IAppState) => state.countryAndProvincesInfo.countryDDL
	);
	const [openDialog, setDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [employeeId, setEmployeeId] = useState<any>(0);
	const [isPendingData, setIsPendingData] = useState(false);
	const [provinceDDLLoading, setProvinceDDLLoading] = useState(false);
	const [provinceStateDDLinUpdate, setProvinceStateDDLinUpdate] =
		useState(undefined);
	const [cityDDLinUpdate, setCityDDLinUpdate] = useState(undefined);
	const [provinceDDLItems, setProvinceDDLItems] = useState(undefined);
	const [selectdSinStatus, setSelectdSinStatus] = useState(100);
	const [sinStatusItems, setSinStatusItems] = useState([
		{ label: "Pending", value: 100 },
		{ label: "Refused", value: 200 },
		{ label: "Failed to Provide", value: 300 },
	]);
	// console.log('companyInformation', companyInformation)

	const [selectedPayFrequency, setSelectedPayFrequency] = useState<any>(null);
	const [selectedCard, setSelectedCard] = useState<any>(null);
	const [itemSearch, setItemSearch] = useState("");
	const [sidePanelsVisibility, setSidePanelsVisibility] = useState(true);
	const [selectedResource, setSelectedResource] = useState("create-new");
	const [selectedResourceList, setSelectedResourceList] = useState<
		IDropDownListResponse[]
	>([]);
	// const [resourceListWithoutCreateNew, setResourceListWithoutCreateNew] =
	//   useState<any>(undefined);
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const [
		sinIncorrectStatusConfirmDialogeOpen,
		setSinIncorrectStatusConfirmDialogeOpen,
	] = useState(false);
	const [sinErrorMsg, setSinErrorMsg] = useState("yes");
	const [provinceStateLabelinUpdate, setProvinceStateLabelinUpdate] =
		useState<any>(undefined);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);

	const { isPending: isPendingStatutoryHolidayFormDataByClientId, data: statutoryHolidayFormDataByClientId } = useGetDataStatutoryHolidayForm(
		clientId,
		navigateToLogout
	);

	const { isPending: isSubmitting, mutate: submitFormFields } = useMutation({
		mutationFn: EmployeeService.create,

		onSuccess: (res: IEmployee | IServerError | IResponse | any) => {


			if (res && res.status === 201 || res && res.status === 200) {
				setEditRecordId(res.data.id);
				// queryClient.invalidateQueries({
				// 	queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, res.data.id],
				// });
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				scrollPanelFieldsToTop();
				let path = `/employee/setup/${res.data.id}`;
				navigate(path);
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: [EMPLOYEE_DDL_DATA_QUERY_KEY],
					}),
					queryClient.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					}),
				]);
			}
			// else if (
			// 	res &&
			// 	res.message === "Success"
			// ) {
			// 	setEditRecordId(res.id);
			// 	queryClient.invalidateQueries({
			// 		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, res.id],
			// 	});
			// 	setSnackbarData({
			// 		open: true,
			// 		message: res.message,
			// 		severity: "success",
			// 	});
			// 	// let path = `/clients/list`;
			// 	// navigate(path);
			// }
			else if (res && res.message === "Success") {
				setSnackbarData({
					open: true,
					message: res.message,
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}

			// set provincesForTDForm in store
			// if (res?.data?.employeePayroll?.provinceOfEmployment?.shortCode) {
			// 	let shortCode = res?.data?.employeePayroll?.provinceOfEmployment?.shortCode
			// 	dispatch(setSelectedDashboardApisData({
			// 		provincesForTDForm: {
			// 			...provincesForTDForm,
			// 			[shortCode]: true,
			// 		}
			// 	}));
			// }
			//Load EMployee DDL
			const fetchData = async () => {
				try {
					const response: any = await T4SlipService.getEmployeeDDL(
						clientId,
					);


					if (response && Array.isArray(response)) {
						let resources: IDropDownListResponse[] = response.map(
							(resourceItem: IDropDownListResponse) => ({
								// id: resourceItem.id,
								label: resourceItem.label,
								value: resourceItem.value,
							})
						);

						// resources.unshift({
						// 	label: "Create New",
						// 	value: "create-new",
						// });

						setSelectedResourceList(sortData(resources));
					}

					// setResourceListWithoutCreateNew(response);
				} catch (error) {
					console.error("Error fetching data:", error);
				} finally {
					setIsPendingData(false);
				}
			};

			fetchData();
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		editRecordId,
		navigateToLogout
	);

	// const { isPending: isPendingCountryDDL, data: countryList } =
	//   useGetCountryDDL();
	const { isPending: isPendingPayFrequencyDDL, data: payFrequencyDDLItems } =
		useGetPayFrequencyDDL(clientId, taxYear);
	const { isPending: isPendingEmployeeDDLItems, data: employeeDDLItems } =
		useGetEmployeeDDLItems(clientId, taxYear);

	const { isPending: isPendingEmployeePermissionsData, data: employeePermissionsData }: any =
		useGetEmployeePermissionData(
			clientId,
			selectedPayFrequency,
			navigateToLogout,
			employeeId

		);

	const { isPending: isPendingClientInformationById, data: companyInformation } = useGetClientInformationById(
		clientId,
		navigateToLogout
	);
	const { isPending: isPendingGeneralSettingsInformationById, data: general_settings } = useGetGeneralSettingsData(
		clientId,
		navigateToLogout
	);

	const onPayFrequencyChangeHandler = (selected: any) => {
		setSelectedPayFrequency(selected.value)
	}

	console.log('employeePermissionsData', employeePermissionsData)

	useEffect(() => {
		if (employeeDDLItems) {
			if (employeeDDLItems && Array.isArray(employeeDDLItems)) {
				let resources: IDropDownListResponse[] = sortDDLByLabel(employeeDDLItems).map(
					(resourceItem: IDropDownListResponse) => ({
						// id: resourceItem.id,
						label: resourceItem.label,
						value: resourceItem.value,
					})
				);

				// resources.unshift({
				//   label: "Create New",
				//   value: "create-new",
				// });

				setSelectedResourceList(sortData(resources));
			}
			// setResourceListWithoutCreateNew(sortData(employeeDDLItems));
		}
	}, [employeeDDLItems]);

	const ExtendedValidationSchema = EmployeeSetupValidationSchema.shape({
		postCode: yup.string().when("country", (country: any, schema: any) => {

			const selectedCountry = countryList.find(
				(c: any) => c.value === country[0]
			);

			if (selectedCountry?.shortCode === "CAN") {
				return schema
					.required("Postal Code is required")
					.length(6, "Postal Code must be exactly 6 characters");
			}
			return schema; // Unchanged for other countries
		}),
		zipCode: yup.string().when("country", (country: any, schema: any) => {
			const selectedCountry = countryList.find(
				(c: any) => c.value === country[0]
			);
			if (selectedCountry?.shortCode === "USA") {
				return schema
					.required("Zip Code is required")
					.length(5, "Zip Code must be exactly 5 characters");
			}
			return schema; // Unchanged for other countries
		}),
		socialInsuranceNumber: yup
			.string()
			.test("is-masked-or-not", "Invalid value", function (value: any) {
				if (value) {
					let sin = value && value.replace(/-/g, "");
					const output = checkSINValidity(sin);

					let maxLength = 11;

					if (value && value.charAt(2) === "*") {
						maxLength = 7;
						if (value && value.length == maxLength) {
							return true;
						} else {
							return this.createError({
								message: "Invalid Social Insuarance Number",
								path: this.path,
							});
						}
					} else {
						if (value && value.length == maxLength && output % 10 === 0) {
							setSinStatusItems([{ label: "Recorded", value: 400 }]);
							setSelectdSinStatus(400);
							formMethods.setValue("sinStatus", 400);
							return true;
						} else if (
							value &&
							value.length == maxLength &&
							output % 10 !== 0
						) {
							if (sinErrorMsg === "yes") {
								setSinIncorrectStatusConfirmDialogeOpen(true);
								return this.createError({
									message: "Invalid Social Insuarance Number",
									// message: 'Social Insuarance Number has at least 9 characters',
									path: this.path,
								});
							} else {
								setSinStatusItems([
									{ label: "Recorded Incorrect", value: 500 },
								]);
								setSelectdSinStatus(500);
								formMethods.setValue("sinStatus", 500);
								return true;
							}
							// return true;
						} else {
							setSinErrorMsg("yes");
							return this.createError({
								message: "Invalid Social Insuarance Number",
								// message: 'Social Insuarance Number has at least 9 characters',
								path: this.path,
							});
						}
					}
				} else {
					setSinStatusItems([
						{ label: "Pending", value: 100 },
						{ label: "Refused", value: 200 },
						{ label: "Failed to Provide", value: 300 },
					]);
					setSelectdSinStatus(100);
					formMethods.setValue("sinStatus", 100);
				}
				// return this.createError({
				//   message: 'Social Insuarance Number is required',
				//   path: this.path,
				// });
				return true;
			})
			.nullable(),
		dateOfHire: yup
			.date()
			.typeError("Invalid date format")
			.required("Date of Hire is required")
			// .nullable()
			.test("is-after-last-period", "Employee's date of hire must be after last processed pay period end date.", function (value) {
				if (id) {
					return true;
				}
				if (!value) return true; // Let required() handle null/undefined

				if (employeePermissionsData && employeePermissionsData.lastPeriodProcessed != null) {
					const lastPeriodDate = new Date(employeePermissionsData.lastPeriodProcessed);
					const dateOfHire = new Date(value);

					if (dateOfHire <= lastPeriodDate) {
						return this.createError({
							message: "Employee's date of hire must be after last processed pay period end date.",
							path: this.path,
						});
					}
				}

				return true;
			}),
	});

	const DEFAULT_VALUES = {
		employeeType: 200,
		employeeId: undefined,
		firstName: undefined,
		lastName: undefined,
		initial: undefined,
		dateOfBirth: null,
		gender: 200,
		addressLine1: undefined,
		country: canada?.value,
		// province: undefined,
		postCode: null,

		email: undefined,
		phone: undefined,
		socialInsuranceNumber: undefined,
		// sinStatus: undefined,  //
		followUpNotes: undefined,
		holidayPayCalculation: 100,
		applyCompanyFormula: 200,
		continuousOperatio: 100,
		workArrangement: 100,
		holidayPayOpenBalance: null,
		payFrequency: undefined,
		dateOfHire: null,
		payMethod: 200,
		provinceOfEmployment: undefined,
		vacationPay: 100,
		payOutVacationPay: 100,
		// vacationPayOpenBalance: "0.00",
		includeInT4OrRl1: 200,
		dentalBenefits: undefined,
		stubLanguage: 100,
		emailStub: 100,
		printHourlyRateOnStub: 100,
		stubNotes: undefined,
		datePensionBecomePayable: null,
		rppOrDpspRegistrationNumber: undefined,
		dateOfDisability: null,
		dateDisabilityWasCeased: null,
		// employeePayrollType: 200,
		regularDailyHours: 8,
	};

	const formMethods = useForm<any>({
		resolver: yupResolver(ExtendedValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES,
	});

	// const scrollToFirstError = (errors: any) => {
	// 	// Get all field names that have errors
	// 	const errorFieldNames = Object.keys(errors);
	// 	if (errorFieldNames.length === 0) return;

	// 	// Build a selector that matches any of the error fields
	// 	const selectors = errorFieldNames
	// 		.map(field => `[name="${field}"], [id="${field}"]`)
	// 		.join(", ");

	// 	// Query all matching elements and pick the first one in DOM order
	// 	// const allErrorElements = document.querySelectorAll(selectors);
	// 	const allErrorElements = Array.from(
	// 		document.querySelectorAll(selectors)
	// 	) as HTMLElement[];

	// 	if (allErrorElements.length === 0) return;

	// 	// const firstElement = allErrorElements[0] as HTMLElement;

	// 	// Skip react-select's hidden `type="hidden"` name-input and any
	// 	// other non-rendered elements, so we land on the real focusable field.
	// 	const focusableErrorElements = allErrorElements.filter((el) => {
	// 		if ((el as HTMLInputElement).type === "hidden") return false;
	// 		return el.offsetParent !== null;
	// 	});

	// 	const firstElement = focusableErrorElements[0] || allErrorElements[0];
	// 	if (!firstElement) return;

	// 	firstElement.scrollIntoView({ behavior: "smooth", block: "center" });
	// 	setTimeout(() => {
	// 		firstElement.focus();
	// 	}, 300);
	// };

	const onSubmit = async (values: any) => {

		// Close the dialog immediately
		setSinIncorrectStatusConfirmDialogeOpen(false);

		const SelectedCountry: any = countryList.find(
			(e: any) => e.value === values.country
		);

		try {
			setIsPendingData(true);
			const ProvinceOrStateDDL = await CountryService.getProvinceOrStateDDL(
				navigateToLogout,
				SelectedCountry.value,
				SelectedCountry.countryType
			);
			const selectedProvinceOrState = values.province
				? ProvinceOrStateDDL.find((e: any) => e.value === values.province)
				: values.state
					? ProvinceOrStateDDL.find((e: any) => e.value === values.state)
					: undefined;

			const selectedPOE =
				values.provinceOfEmployment &&
				ProvinceOrStateDDL.find(
					(e: any) => e.value === values.provinceOfEmployment
				);

			// const selectedPayFrequencyValue = formMethods.watch("payFrequency");
			const selectedPayFrequency = payFrequencyDDLItems && payFrequencyDDLItems.find((x: any) => x.value === values.payFrequency)

			let payload = {
				id: id && id,
				navigateToLogout: navigateToLogout,
				clientId: clientId,
				employeeType: values.employeeType,
				employeeId: values.employeeId,
				firstName: values.firstName,
				lastName: values.lastName,
				initial: values.initial,
				gender: values.gender,
				dateOfBirth:
					values.dateOfBirth !== "" && formatDate(values.dateOfBirth),
				// socialInsuranceNumber: values.socialInsuranceNumber,
				socialInsuranceNumber: values.socialInsuranceNumber && values.socialInsuranceNumber.replace(
					/-/g,
					""
				),
				sinStatus: values.sinStatus,
				followUpNotes: values.followUpNotes,
				address: {
					countryType: values.province ? true : false,
					country: {
						id: SelectedCountry.value,
						name: SelectedCountry.label,
						shortCode: SelectedCountry.shortCode,
					},
					province: values.province
						? {
							id: selectedProvinceOrState.value,
							name: selectedProvinceOrState.label,
							shortCode: selectedProvinceOrState.shortCode,
						}
						: undefined,
					states: values.state
						? {
							id: selectedProvinceOrState.value,
							name: selectedProvinceOrState.label,
							shortCode: selectedProvinceOrState.shortCode,
						}
						: undefined,
					addressLine1: values.addressLine1,
					city: values.city,
					// postCode: values.postCode ? values.postCode : undefined,
					postCode: values.postCode
						? values.postCode.replace(/[a-z]/g, (match: string) =>
							match.toUpperCase()
						)
						: undefined,
					zipCode: values.zipCode ? values.zipCode : undefined,
					email: values.email !== "" ? values.email : undefined,
					phone: values.phone,
				},
				employeeSetub: {
					stubLanguage: values.stubLanguage,
					emailStub: values.emailStub,
					printHourlyRateOnStub: values.printHourlyRateOnStub,
					stubNotes: values.stubNotes,
				},
				employeeBank: {
					branchTransitNumber: values.branchTransitNumber,
					branchTransitNumberForAccount2: values.branchTransitNumberForAccount2,
					bankFinancialNo: values.bankFinancialNo,
					bankFinancialNoForAccount2: values.bankFinancialNoForAccount2,
					// bankInstitutionNumber: values.bankInstitutionNumber,
					// bankInstitutionNumberForAccount2: values.bankInstitutionNumberForAccount2,
					accountNo: values.accountNo,
					account2No: values.account2No,
					account1Name: values.account1Name,
					account2Name: values.account2Name,
					split: values.split,
					percenagePerDollorInAccount1: parseInt(
						values.percenagePerDollorInAccount1
					),
				},
				employeePayroll: {
					payFrequency: values.payFrequency,
					payPeriod: selectedPayFrequency.payPeriodCode,
					dateOfHire: values.dateOfHire !== "" &&
						formatDate(values.dateOfHire),
					payMethod: values.payMethod,
					provinceOfEmployment: selectedPOE
						? {
							id: selectedPOE.value,
							name: selectedPOE.label,
							shortCode: selectedPOE.shortCode,
						}
						: undefined,
					// dateOfTermination:
					// 	values.dateOfTermination !== null
					// 		? formatDate(values.dateOfTermination)
					// 		: undefined,
					includeInT4OrRl1: values.includeInT4OrRl1,
					vacationPay: values.vacationPay,
					payOutVacationPay: values.payOutVacationPay,
					vacationPayOpenBalance:
						values.vacationPayOpenBalance &&
						parseFloat(values.vacationPayOpenBalance),
					dentalBenefits: values.dentalBenefits,




					statutoryHolidaysBasedOn: values.statutoryHolidaysBasedOn,
					holidayPayCalculation: values.holidayPayCalculation,
					applyCompanyFormula: values.applyCompanyFormula,
					continuousOperation: values.continuousOperation,
					workArrangement: values.workArrangement,
					holidayPayOpenBalance: values.holidayPayOpenBalance,
				},
				additionalInformation: {
					datePensionBecomePayable: values.datePensionBecomePayable !== "" && formatDate(
						values.datePensionBecomePayable
					),
					rppOrDpspRegistrationNumber: values.rppOrDpspRegistrationNumber,
					dateOfDisability: values.dateOfDisability !== "" && formatDate(values.dateOfDisability),
					dateDisabilityWasCeased: values.dateDisabilityWasCeased !== "" && formatDate(values.dateDisabilityWasCeased),
					employeePayrollType: values.employeePayrollType,
					regularDailyHours: values.employeePayrollType === 200 ? values.regularDailyHours : undefined
				},
			};
			submitFormFields(payload);
		} catch {

		} finally {
			setIsPendingData(false);
		}
	};

	useEffect(() => {

		if (id && selectedDataRecord) {
			formMethods.clearErrors()
			setEmployeeId(selectedDataRecord.id)
			Object.keys(selectedDataRecord).forEach((fieldName) => {
				// @ts-ignore
				const fieldValue = selectedDataRecord[fieldName];
				// @ts-ignore
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === "address") {
					Object.keys(fieldValue).forEach((objFieldName) => {
						const objFieldValue = fieldValue[objFieldName];
						formMethods.setValue(objFieldName, objFieldValue ?? undefined);
						if (objFieldName === "countryType") {
							(async () => {
								try {
									setProvinceDDLLoading(true);
									const responce = await CountryService.getProvinceOrStateDDL(
										navigateToLogout,
										selectedDataRecord.address.country.id,
										objFieldValue ? "province" : "state"
									);

									setProvinceStateDDLinUpdate(responce);
									setProvinceStateLabelinUpdate(
										objFieldValue ? "province" : "state"
									);
								} catch {

								} finally {
									setProvinceDDLLoading(false);
								}
							})();
						}
						if (objFieldName === "country") {
							formMethods.setValue(objFieldName, objFieldValue.id ?? undefined);
						}
						if (objFieldName === "province") {
							formMethods.setValue(objFieldName, objFieldValue.id ?? undefined);
							(async () => {
								try {
									setProvinceDDLLoading(true);
									const responce = await CityService.getCityDDL(
										navigateToLogout,
										objFieldValue.id
									);

									setCityDDLinUpdate(responce);
								} catch {

									setProvinceDDLLoading(false);
								} finally {
									setProvinceDDLLoading(false);
								}
							})();
						}
						if (objFieldName === "state") {
							formMethods.setValue(objFieldName, objFieldValue.id ?? undefined);
						}
						if (objFieldName === "city") {
							// formMethods.setValue(objFieldName, objFieldValue.id ?? undefined);
							formMethods.setValue(objFieldName, objFieldValue ?? undefined);
						}
					});
				}
				if (fieldName === "dateOfBirth") {
					if (fieldValue !== null && fieldValue !== undefined) {
						if (fieldValue.charAt(2) === "*") {
							if (fieldValue.endsWith("0")) {
								formMethods.setValue(fieldName, "1*****4");
							} else { formMethods.setValue(fieldName, fieldValue); }

						} else {
							const defaultDate = new Date(fieldValue);

							formMethods.setValue(fieldName, defaultDate);
						}
					} else if (fieldValue === null) {
						const maskedStatus: any = sessionStorage.getItem("maskEnabled");
						if (maskedStatus === "true") {
							formMethods.setValue(fieldName, "0*****0");
						}
					}
				}
				if (fieldName === "employeeSetub") {
					Object.keys(fieldValue).forEach((objFieldName) => {
						const objFieldValue = fieldValue[objFieldName];
						formMethods.setValue(objFieldName, objFieldValue ?? undefined);
					});
				}
				if (fieldName === "employeeBank") {
					Object.keys(fieldValue).forEach((objFieldName) => {
						const objFieldValue = fieldValue[objFieldName];
						formMethods.setValue(objFieldName, objFieldValue ?? undefined);
					});
				}
				if (fieldName === "employeePayroll") {
					Object.keys(fieldValue).forEach((objFieldName) => {
						const objFieldValue = fieldValue[objFieldName];
						formMethods.setValue(objFieldName, objFieldValue ?? undefined);
						if (objFieldName === "provinceOfEmployment") {
							formMethods.setValue(objFieldName, objFieldValue.id ?? undefined);
						}
						if (objFieldName === "dateOfHire") {
							if (fieldValue !== null && fieldValue !== undefined) {
								const defaultDate = new Date(objFieldValue);

								formMethods.setValue(objFieldName, defaultDate);
							}
						}
						// if (objFieldName === "dateOfTermination") {
						// 	if (fieldValue !== null && fieldValue !== undefined) {
						// 		const defaultDate = new Date(objFieldValue);

						// 		formMethods.setValue(objFieldName, defaultDate);
						// 	}
						// }
						if (objFieldName === "payFrequency") {
							setSelectedPayFrequency(objFieldValue)
							formMethods.setValue(objFieldName, objFieldValue);
						}
					});
				}
				if (fieldName === "additionalInformation" && fieldValue) {
					Object.keys(fieldValue).forEach((objFieldName) => {
						const objFieldValue = fieldValue[objFieldName];
						// formMethods.setValue(objFieldName, objFieldValue ?? undefined);
						if (objFieldName === "rppOrDpspRegistrationNumber") {
							formMethods.setValue(objFieldName, objFieldValue ?? undefined);
						}
						if (objFieldName === "datePensionBecomePayable") {
							if (objFieldValue !== null && objFieldValue !== undefined) {
								const defaultDate = new Date(objFieldValue);

								formMethods.setValue(objFieldName, defaultDate);
							}
						}
						if (objFieldName === "dateOfDisability") {
							if (objFieldValue !== null && objFieldValue !== undefined) {
								const defaultDate = new Date(objFieldValue);

								formMethods.setValue(objFieldName, defaultDate);
							}
						}
						if (objFieldName === "dateDisabilityWasCeased") {
							if (objFieldValue !== null && objFieldValue !== undefined) {
								const defaultDate = new Date(objFieldValue);

								formMethods.setValue(objFieldName, defaultDate);
							}
						}
						if (objFieldName === "employeePayrollType") {
							formMethods.setValue(objFieldName, objFieldValue ?? undefined);
						}
						if (objFieldName === "regularDailyHours") {
							formMethods.setValue(objFieldName, objFieldValue ?? undefined);
						}
					});
				} else if (fieldName === "additionalInformation" && !fieldValue) {
					formMethods.setValue("rppOrDpspRegistrationNumber", undefined);
					formMethods.setValue("datePensionBecomePayable", null);
					formMethods.setValue("dateOfDisability", null);
					formMethods.setValue("dateDisabilityWasCeased", null);
					formMethods.setValue("employeePayrollType", 200);
					formMethods.setValue("regularDailyHours", null);
				}
				if (fieldName === "sinStatus") {
					if (fieldValue === 500) {
						setSinStatusItems([{ label: "Recorded Incorrect", value: 500 }]);
						setSelectdSinStatus(fieldValue);
						formMethods.setValue("sinStatus", fieldValue);
					} else if (fieldValue === 400) {
						setSinStatusItems([{ label: "Recorded", value: 400 }]);
						setSelectdSinStatus(fieldValue);
						formMethods.setValue("sinStatus", fieldValue);
					} else {
						setSinStatusItems([
							{ label: "Pending", value: 100 },
							{ label: "Refused", value: 200 },
							{ label: "Failed to Provide", value: 300 },
						]);
						setSelectdSinStatus(fieldValue);
						formMethods.setValue("sinStatus", fieldValue);
					}
				}
			});
		}
	}, [id, selectedDataRecord]);

	useEffect(() => {
		if (general_settings && !selectedDataRecord && !id) {
			formMethods.setValue('regularDailyHours', general_settings.regularDailyHoursForEmployees)
			formMethods.setValue('vacationPay', general_settings.vacationPayOnPaidVacPay)
			formMethods.setValue('payoutVacationPay', general_settings.payoutVacationPay)
		}
		if (general_settings && selectedDataRecord && id) {
			console.log('yes')
			if (general_settings.vacationPayOnPaidVacPay === 200) {
				formMethods.setValue('vacationPay', general_settings.vacationPayOnPaidVacPay)
				formMethods.setValue('payoutVacationPay', general_settings.payoutVacationPay)
			} else if (selectedDataRecord) {
				formMethods.setValue('vacationPay', selectedDataRecord.employeePayroll.vacationPay)
				formMethods.setValue('payoutVacationPay', selectedDataRecord.employeePayroll.payOutVacationPay)
			}
			// formMethods.setValue('vacationPay', general_settings.vacationPayOnPaidVacPay)
			// formMethods.setValue('payoutVacationPay', general_settings.payoutVacationpay)
		}
	}, [selectedDataRecord, general_settings, id])

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteEmployeeById } = useMutation({
		mutationFn: EmployeeService.delEmployee,

		onSuccess: (res: string | IServerError | IResponse | any) => {
			if (typeof res === "string") {
				// remove the cache of the Employee fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message ? res.message : "Something went wrong.",
					severity: res.status === false ? "error" : res.success === false ? "error" : "success",
				});
			}
			// formMethods.reset();
			scrollPanelFieldsToTop();
			let path = `/employee/list`;
			navigate(path);
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const deleteEmployeeField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				employeeId: employeeId,
				navigateToLogout: navigateToLogout,
			};
			deleteEmployeeById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setEmployeeId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		setSelectedPayFrequency(null);
		// formMethods.setValue("country", "Canada");
		// formMethods.setValue("province","");
		setCityDDLinUpdate(undefined)
		scrollPanelFieldsToTop();
		let path = `/employee/setup`;
		navigate(path);
	};

	const onCloseSinIncorrectSattusDialogeHandler = () => {
		setSinIncorrectStatusConfirmDialogeOpen(false);
		setSinErrorMsg("yes");
	};

	const onClickInvalidSinStatusClickedHandler = () => {
		setSinIncorrectStatusConfirmDialogeOpen(false);
		setSinErrorMsg("no");

		// Close the dialog immediately
		// setSinIncorrectStatusConfirmDialogeOpen((prev) => !prev);

		setTimeout(() => {
			formMethods.handleSubmit(onSubmit)();
		}, 100);
		// Trigger form submission
		// setSinStatusItems([{ label: "Recorded", value: 300 }]);
		// setSelectdSinStatus(300);
	};

	//   useEffect(() => {
	// 	if (!sinIncorrectStatusConfirmDialogeOpen) {
	// 		formMethods.handleSubmit(onSubmit)();
	// 	}
	// }, [sinIncorrectStatusConfirmDialogeOpen]);

	// const { isPending, data } = useAccountants({
	//   search_query: "",
	//   order_by: "acc_title",
	//   order_direction: "asc",
	//   page: 1,
	//   size: 25,
	// });

	const onResourceSelectionChangeHandler = async (
		selectedItem: any,
		index?: any
	): Promise<void> => {

		//this is for card selection
		setSelectedCard(index);
		if (!index) {
			//this is for card selection
			setSelectedCard(selectedItem.value);
		}
		if (selectedItem.value === "create-new") {
			setSnackbarData({
				open: true,
				message: "Please load an employee",
				severity: "error",
			});
			setSidePanelsVisibility((prevButtonState: any) => !prevButtonState);
			formMethods.reset();
			let path = `/employee/setup`;
			navigate(path);
			setSelectedResource("create-new");
		} else {
			let path = `/employee/setup/${selectedItem.value}`;
			navigate(path);
			setEditRecordId(selectedItem.value);
			queryClient.invalidateQueries({
				queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
			});
			// (async () => {
			//   try {
			// 	setIsPendingData(true);
			// 	let resourceDataById =
			// 	  await EmployeeService.getById(
			// 		selectedItem.value,
			// 		navigateToLogout
			// 	  );

			// 	if (resourceDataById) {
			// 		Object.keys(selectedDataRecord).forEach((fieldName) => {
			// 			// @ts-ignore
			// 			const fieldValue = selectedDataRecord[fieldName];
			// 			// @ts-ignore
			// 			formMethods.setValue(fieldName, fieldValue ?? undefined);
			// 			if (fieldName === "address") {
			// 				Object.keys(fieldValue).forEach((objFieldName) => {
			// 					const objFieldValue = fieldValue[objFieldName];
			// 					formMethods.setValue(objFieldName, objFieldValue ?? undefined);
			// 					if (objFieldName === "countryType") {
			// 						(async () => {
			// 							try {
			// 								setProvinceDDLLoading(true);
			// 								const responce = await CountryService.getProvinceOrStateDDL(
			// 									navigateToLogout,
			// 									selectedDataRecord.address.country.id,
			// 									objFieldValue ? "province" : "state"
			// 								);

			// 								setProvinceStateDDLinUpdate(responce);
			// 								setProvinceStateLabelinUpdate(
			// 									objFieldValue ? "province" : "state"
			// 								);
			// 							} catch {
			// 							
			// 							} finally {
			// 								setProvinceDDLLoading(false);
			// 							}
			// 						})();
			// 					}
			// 					if (objFieldName === "country") {
			// 						formMethods.setValue(objFieldName, objFieldValue.id ?? undefined);
			// 					}
			// 					if (objFieldName === "province") {
			// 						formMethods.setValue(objFieldName, objFieldValue.id ?? undefined);
			// 					}
			// 					if (objFieldName === "state") {
			// 						formMethods.setValue(objFieldName, objFieldValue.id ?? undefined);
			// 					}
			// 				});
			// 			}
			// 			if (fieldName === "dateOfBirth") {
			// 				if (fieldValue !== null && fieldValue !== undefined) {
			// 					if (fieldValue.charAt(2) === "*") {
			// 						formMethods.setValue(fieldName, fieldValue);
			// 					} else {
			// 						const defaultDate = new Date(fieldValue);
			// 					
			// 						formMethods.setValue(fieldName, defaultDate);
			// 					}
			// 				} else if (fieldValue === null) {
			// 					const maskedStatus: any = sessionStorage.getItem("maskEnabled");
			// 					if (maskedStatus === "true") {
			// 						formMethods.setValue(fieldName, "0*****0");
			// 					}
			// 				}
			// 			}
			// 			if (fieldName === "employeeSetub") {
			// 				Object.keys(fieldValue).forEach((objFieldName) => {
			// 					const objFieldValue = fieldValue[objFieldName];
			// 					formMethods.setValue(objFieldName, objFieldValue ?? undefined);
			// 				});
			// 			}
			// 			if (fieldName === "employeeBank") {
			// 				Object.keys(fieldValue).forEach((objFieldName) => {
			// 					const objFieldValue = fieldValue[objFieldName];
			// 					formMethods.setValue(objFieldName, objFieldValue ?? undefined);
			// 				});
			// 			}
			// 			if (fieldName === "employeePayroll") {
			// 				Object.keys(fieldValue).forEach((objFieldName) => {
			// 					const objFieldValue = fieldValue[objFieldName];
			// 					formMethods.setValue(objFieldName, objFieldValue ?? undefined);
			// 					if (objFieldName === "provinceOfEmployment") {
			// 						formMethods.setValue(objFieldName, objFieldValue.id ?? undefined);
			// 					}
			// 					if (objFieldName === "dateOfHire") {
			// 						if (fieldValue !== null && fieldValue !== undefined) {
			// 							const defaultDate = new Date(objFieldValue);
			// 				
			// 							formMethods.setValue(objFieldName, defaultDate);
			// 						}
			// 					}
			// 					if (objFieldName === "dateOfTermination") {
			// 						if (fieldValue !== null && fieldValue !== undefined) {
			// 							const defaultDate = new Date(objFieldValue);
			// 							
			// 							formMethods.setValue(objFieldName, defaultDate);
			// 						}
			// 					}
			// 				});
			// 			}
			// 		});
			// 	}
			//   } catch (error) {
			// 	  console.error("Error fetching data:", error);
			// } finally {
			// 	  setIsPendingData(false);
			//   }
			// })();
		}
	};

	// useEffect(() => {
	//   //Load EMployee DDL
	//   setIsPendingData(true);
	//   const fetchData = async () => {
	//     try {
	//       const response: any = await T4SlipService.getEmployeeDDL(
	//         clientId,
	//         // "Employee",
	//         2024
	//       );
	//   
	//       if (response && Array.isArray(response)) {
	//         let resources: IDropDownListResponse[] = response.map(
	//           (resourceItem: IDropDownListResponse) => ({
	//             // id: resourceItem.id,
	//             label: resourceItem.label,
	//             value: resourceItem.value,
	//           })
	//         );

	//         // resources.unshift({
	//         // 	label: "Create New",
	//         // 	value: "create-new",
	//         // });

	//         setSelectedResourceList(resources);
	//       }
	// 
	//       setResourceListWithoutCreateNew(response);
	//     } catch (error) {
	//       console.error("Error fetching data:", error);
	//     } finally {
	//       setIsPendingData(false);
	//     }
	//   };

	//   fetchData();
	// }, []);

	const upadtedEmployeeListItems = useMemo(() => {
		return (
			selectedResourceList &&
			sortDDLByLabel(selectedResourceList).map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/employee/setup/${item.value}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => {
							setEditRecordId(item.value);
							scrollPanelFieldsToTop();
						}}
					>
						{item.label}
					</Link>
				),
				// id:item.value
			}))
		);
	}, [selectedResourceList]);




	// Load Employee For mobile Screen
	const updatedEmployeeDDLItems = useMemo(() => {
		if (selectedResourceList) {
			if (
				selectedResourceList &&
				Array.isArray(selectedResourceList)
			) {
				let items =
					selectedResourceList.map(
						(item: any) => ({
							label: item.label,
							value: item.value,
						})
					);

				items.unshift({
					label: "--",
					value: null,
				});

				return items;
			}
			return [];
		}
	}, [selectedResourceList]);
	const onEmployeeSelectionChangeHandler = (selected: any) => {
		setEditRecordId(selected.value);
		scrollPanelFieldsToTop();
		if (selected.value) navigate(`/employee/setup/${selected.value}`)
	};

	// const filteredResourceList =
	//   resourceListWithoutCreateNew &&
	//   resourceListWithoutCreateNew.filter((item: any) =>
	//     item.label.toLowerCase().includes(itemSearch.toLowerCase())
	//   );

	const onCreateNewButtonClicked = () => {
		setIsPendingData(true);
		setTimeout(() => {
			setIsPendingData(false);
		}, 1000);
		formMethods.reset();
		let path = `/employee/setup`;
		navigate(path);
		setSelectedResource("create-new");
		setSelectedCard(null);
	};

	const getCardStyles = (index: any) => {

		return {
			borderLeft:
				selectedCard === index ? "6px solid #0091b3" : "2px solid transparent",
			cursor: "pointer",
		};
	};
	const onDividerButtonClickHandler = () => {
		setSidePanelsVisibility((prevButtonState: any) => !prevButtonState);
	};

	const expandedLabels = ["Employee"];
	const expandedEmployeeListOrSetupItem = "Setup";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Employee" &&
				item.label === expandedEmployeeListOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedEmployeeListOrSetupItem && "/employee/setup",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: id ? id : "",
				entityId2: "",
				entityId3: "",
				screenName: CLIENT_SETUP_SCREEN_SLUG,
				accountantName: clientName,
				activityType:
					"activity_type=create&activity_type=updated&activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const onSubmitAllTabs = (formData: any) => {

	};

	console.log("statutoryHolidayFormDataByClientId", statutoryHolidayFormDataByClientId)

	return (
		<Grid>
			<BackdropCircularProgress
				open={
					isSubmitting ||
					isPendingById ||
					isDeleting ||
					isPendingData ||
					isPendingActivityLogData ||
					isPendingPayFrequencyDDL ||
					provinceDDLLoading || isPendingEmployeeDDLItems ||
					isPendingEmployeePermissionsData ||
					isPendingClientInformationById ||
					isPendingGeneralSettingsInformationById ||
					isPendingStatutoryHolidayFormDataByClientId
				}
				testId="accountant Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteEmployeeField}
				cancelActionHandler={onCloseHandler}
				testId="accountant-confirmation-dialog"
			/>
			{companyInformation && companyInformation.status === 100 && payFrequencyDDLItems && payFrequencyDDLItems.length ? <Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
					<FormProvider {...formMethods}>
						<form onSubmit={formMethods.handleSubmit(onSubmit)}>
							<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
								<Grid
									size={{ xs: 12, sm: 12, md: 8.5, lg: 8, xl: 8 }}
									sx={{
										// border: "1px solid rgba(0, 0, 0, 0.12)",
										// borderRadius: "3px",
										// marginTop: "16px",
										background: "#ffffff",
									}}
								>
									<TwoColumnLayout
										leftColumnContent={
											<>
												<StandardPanel
													title="Create Employee"
													testId={`employee`}
													variant="outlined"
													borderBottom={true}
													borderHide={true}
												>
													<EmployeeInfoSetupFieldsPanel sequenceNumber={id && selectedDataRecord && selectedDataRecord.sequenceNumber}
														employeePermissionsData={employeePermissionsData && employeePermissionsData}
														employeeDDL={updatedEmployeeDDLItems && updatedEmployeeDDLItems} onEmployeeSelectionChangeHandler={onEmployeeSelectionChangeHandler} />
												</StandardPanel>
												<StandardPanel
													title="Address Info"
													testId={`client-address-info`}
													variant="outlined"
													borderBottom={true}
													borderHide={true}
												>
													<EmployeeAddressSetupFieldsPanel
														testId={"client-address-info"}
														countryList={countryList}
														formMethods={formMethods}
														provinceDDLLoading={(value: boolean) =>
															setProvinceDDLLoading(value)
														}
														provinceStateLabelinUpdate={
															provinceStateLabelinUpdate &&
															provinceStateLabelinUpdate
														}
														provinceStateDDLinUpdate={
															provinceStateDDLinUpdate &&
															provinceStateDDLinUpdate
														}
														cityDDLinUpdate={cityDDLinUpdate && cityDDLinUpdate && sortDDLByLabel(cityDDLinUpdate)}
														provinceDDLItems={(value: any) =>
															setProvinceDDLItems(value)
														}
														isAdminUser={isAdminUser}
														loadedEmployeeId={id}
													/>
												</StandardPanel>
												<StandardPanel
													title="Social Insurance Number"
													testId={`client-address-info`}
													// borderBottom={true}
													borderHide={true}
													variant="outlined"
												>
													<EmployeeSINSetupFieldsPanel
														sinStatusItems={sinStatusItems}
														selectdSinStatus={selectdSinStatus}
														formMethods={formMethods}
														testId={"sin-setup"}
													/>
												</StandardPanel>
												{statutoryHolidayFormDataByClientId && statutoryHolidayFormDataByClientId.calculateStatutoryHoliday === 200 && <StandardPanel
													title="Statutory Holiday"
													testId={`client-address-info`}
													// borderBottom={true}
													borderTop={true}
													borderHide={true}
													variant="outlined"
												>
													<EmployeeStatutoryHolidaySetupFieldsPanel
														formMethods={formMethods}
														selectedDataRecord={
															selectedDataRecord && selectedDataRecord
														}
														testId={"sin-setup"}
													/>
												</StandardPanel>}
											</>
										}
										rightColumnContent={
											<>
												<StandardPanel
													title="Payroll"
													testId={`client-payroll-info`}
													variant="outlined"
													borderBottom={true}
													borderHide={true}
												>
													<EmployeePayrollSetupFieldsPanel
														testId={"client-Payroll-info"}
														isAdminUser={isAdminUser}
														countryList={countryList && countryList}
														vacationPayFromGetById={
															selectedDataRecord &&
															selectedDataRecord?.employeePayroll
														}
														loadedEmployeeId={id}
														payFrequencyDDLItems={
															payFrequencyDDLItems && sortDDLByLabel(payFrequencyDDLItems)
														}
														onPayFrequencyChangeHandler={onPayFrequencyChangeHandler}
														employeePermissionsData={employeePermissionsData && employeePermissionsData}
														companyInformation={companyInformation}
														general_settings={general_settings}
														id={id}
													/>
												</StandardPanel>
												<StandardPanel
													title="Stub"
													testId={`client-Stub-info`}
													borderBottom={true}
													borderHide={true}
													variant="outlined"
												>
													<EmployeeStubSetupFieldsPanel
														testId={"client-Stub-info"}
														isAdminUser={isAdminUser}
													/>
												</StandardPanel>
												<StandardPanel
													title="Additional Information"
													testId={`additional-information`}
													borderHide={true}
													variant="outlined"
												>
													<EmployeeAdditionalInformationFieldsPanel
														formMethods={formMethods}
														testId={"sin-setup"}
													/>
												</StandardPanel>
												{/* <StandardPanel
													title="Bank"
													testId={`client-Bank-info`}
													borderHide={true}
													variant="outlined"
												>
													<EmployeeBankSetupFieldsPanel
														testId={"client-Bank-info"}
														isAdminUser={isAdminUser}
													/>
												</StandardPanel> */}
											</>
										}
										toolbar={
											<BasicSetupFormToolbar
												onResetClicked={onResetClicked}
												onDeleteClicked={onDeleteClicked}
												isSubmitting={isSubmitting}
												id={id}
												pageTitle={"Employee"}
											/>
										}
										testId="form-layout"
										showBorder={true}
									// toolbarPosition="top"
									/>
								</Grid>
								<Grid size={{ xs: 12, sm: 12, md: 0.2, lg: 1, xl: 1 }}></Grid>
								<Grid
									size={{ xs: 12, sm: 12, md: 3.3, lg: 3, xl: 3 }}
									sx={{ marginTop: "16px", display: { xs: "none", sm: "none", md: "block" } }}
								>
									<DDLDataTable
										rows={selectedResourceList && upadtedEmployeeListItems}
										// onRowEditClicked={onRowEditClicked}
										// onRowDeleteClicked={onRowDeleteClicked}
										// onRowArchiveClicked={onArchiveClicked}
										showDeleteIcon={false}
										isLoading={false}
										totalRecords={0}
										initialPageSize={INITIAL_PAGE_SIZE}
										rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
										// onRequestParamsChange={onRequestParamsChange}
										tableScrollHeight={"60vh"}
										showPaginator={false}
										selectedId={selectedDataRecord && id && selectedDataRecord.id}
									/>
								</Grid>
								{/* <Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid> */}
							</Grid>
						</form>
					</FormProvider>
					{/* <StepperForm
						onSubmit={onSubmit}
						stepperTabs={stepperTabs}
						stepperForms={[
							{
								form: (
									<TwoColumnLayout
										leftColumnContent={
											<>
												<StandardPanel
													title="Load Employee"
													testId={`load-slips-panel`}
													variant="outlined"
													borderBottom={true}
													borderHide={true}
												>
													<Grid
														container
														columnSpacing={2}
														size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
													>
														<Grid
															size={{
																xs: 12,
																sm: 12,
																md: 12,
																lg: 12,
																xl: 12,
															}}
														>
															<EnhancedDropDownRHFWithLabel
																label="Load Employee"
																selectName="selectedEmployee"
																items={selectedResourceList}
																selectedItem={selectedResource}
																labelPosition={LabelPosition.Top}
																labelColumns={4}
																fieldColumns={8}
																testId="selected-employee"
																onSelectionChangeHandler={
																	onResourceSelectionChangeHandler
																}
															/>
														</Grid>
													</Grid>
												</StandardPanel>
												<StandardPanel
													title="Create Employee"
													testId={`employee`}
													variant="outlined"
													borderHide={true}
												>
													<EmployeeInfoSetupFieldsPanel />
												</StandardPanel>
											</>
										}
										rightColumnContent={
											<>
												<StandardPanel
													title="Address Info"
													testId={`client-address-info`}
													variant="outlined"
													borderBottom={true}
													borderHide={true}
												>
													<EmployeeAddressSetupFieldsPanel
														testId={"client-address-info"}
														countryList={countryList}
														formMethods={formMethods}
														provinceDDLLoading={(value: boolean) =>
															setProvinceDDLLoading(value)
														}
														provinceStateLabelinUpdate={
															provinceStateLabelinUpdate &&
															provinceStateLabelinUpdate
														}
														provinceStateDDLinUpdate={
															provinceStateDDLinUpdate &&
															provinceStateDDLinUpdate
														}
														provinceDDLItems={(value: any) =>
															setProvinceDDLItems(value)
														}
														isAdminUser={isAdminUser}
													/>
												</StandardPanel>
												<StandardPanel
													title="Social Insurance Number"
													testId={`client-address-info`}
													variant="outlined"
													borderBottom={true}
													borderHide={true}
												>
													<EmployeeSINSetupFieldsPanel
														sinStatusItems={sinStatusItems}
														selectdSinStatus={selectdSinStatus}
														formMethods={formMethods}
														testId={"sin-setup"}
													/>
												</StandardPanel>
												<StandardPanel
													title="Additional Information"
													testId={`additional-information`}
													variant="outlined"
													borderHide={true}
												>
													<EmployeeAdditionalInformationFieldsPanel
														formMethods={formMethods}
														testId={"sin-setup"}
													/>
												</StandardPanel>
											</>
										}
										toolbar={<></>}
										testId="form-layout"
										toolbarPosition="bottom"
										showToolbar={false}
									/>
								),
								validation:PersonalInfoTabValidationSchema,
							},
							{
								form: (
									<TwoColumnLayout
										leftColumnContent={
											<>
												<StandardPanel
													title="Payroll"
													testId={`client-payroll-info`}
													variant="outlined"
													borderHide={true}
												>
													<EmployeePayrollSetupFieldsPanel
														testId={"client-Payroll-info"}
														isAdminUser={isAdminUser}
														countryList={countryList && countryList}
														vacationPayFromGetById={
															selectedDataRecord &&
															selectedDataRecord?.employeePayroll
														}
													/>
												</StandardPanel>
											</>
										}
										rightColumnContent={
											<>
												<StandardPanel
													title="Stub"
													testId={`client-Stub-info`}
													variant="outlined"
													borderHide={true}
												>
													<EmployeeStubSetupFieldsPanel
														testId={"client-Stub-info"}
														isAdminUser={isAdminUser}
													/>
												</StandardPanel>
											</>
										}
										toolbar={<></>}
										testId="form-layout"
										toolbarPosition="bottom"
										showToolbar={false}
									/>
								),
								validation:payrollTabValidationSchema,
							},
							{
								form: (
									<SingleColumnLayout
										toolbar={<></>}
										testId={"bank-single-column-layout"}
										showToolbar={false}
									>
										<StandardPanel
											title="Bank"
											testId={`client-Bank-info`}
											variant="outlined"
											borderHide={true}
										>
											<EmployeeBankSetupFieldsPanel
												testId={"client-Bank-info"}
												isAdminUser={isAdminUser}
											/>
										</StandardPanel>
									</SingleColumnLayout>
								),
								validation:bankTabValidationSchema,
							},
						]}
					/> */}
				</Grid>
			</Grid> : companyInformation && companyInformation.status === 200 ? <>
				<EnhancedSnackbar
					message={"Only available in Active account."}
					onCloseHandler={onSnackbarClosed}
					severity={"warning"}
					testId={"accountant-snackbar"}
					open={true}
					autoHide={false}
				/>
			</> : payFrequencyDDLItems && payFrequencyDDLItems.length === 0 ? <>
				<EnhancedSnackbarForLinkMessage
					message={(
						<>
							Save payroll schedule before creating new employee.{" "}
							<Link
								to={`/company/payroll-schedule`}
								className="no-underline"
								style={{ color: theme.palette.primary.light }}
							>
								click here
							</Link>
						</>
					)}
					onCloseHandler={onSnackbarClosed}
					severity={"warning"}
					testId={"snackbar"}
					open={true}
					autoHide={false}
				/>
			</> : <></>}

			{/* sinIncorrectStatusConfirmDialogeOpen */}
			<EnhancedDialog
				dialogTitle="Confirm"
				key="dialog-box"
				testId="sin-Incorrect-Status-Confirm-Dialoge-Open"
				onCloseHandler={onCloseSinIncorrectSattusDialogeHandler}
				open={sinIncorrectStatusConfirmDialogeOpen}
				dialogContent={
					<Typography>SIN is invalid. Save as "Recorded Incorrect"?</Typography>
				}
				dialogAction={
					<Grid>
						<Button
							autoFocus
							variant="contained"
							onClick={onCloseSinIncorrectSattusDialogeHandler}
							sx={{
								fontSize: "small",
								backgroundColor: "#e0e0e0",
								"&:hover": {
									backgroundColor: "#bdbdbd",
								},
								color: "#212121",
							}}
						>
							No
						</Button>
						<Button
							autoFocus
							variant="contained"
							onClick={onClickInvalidSinStatusClickedHandler}
							sx={{
								fontSize: "small",
								background: `${theme.palette.primary.main}`,
								"&.MuiButton-root:hover": {
									background: `${theme.palette.primary.dark}`,
								},
							}}
							style={{ marginLeft: "5px" }}
						>
							Yes
						</Button>
					</Grid>
				}
			/>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"accountant-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
